import { PlusIcon } from "@heroicons/react/24/outline";
import { usePostHog } from "posthog-js/react";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { EmptyTableMessage } from "@/core/components/empty-table-message";
import { HiddenWhenSpiffOnly } from "@/core/components/hidden-when-spiff-only";
import { ReferralTable } from "@/core/components/referral-table/referral-table";
import { SearchInput } from "@/core/components/search-input";
import { SwitchWithLabel } from "@/core/components/switch-with-label";
import { Button } from "@/core/components/ui/button";
import { LoadingCount } from "@/core/components/ui/loading-count/loading-count";
import { Stack } from "@/core/components/ui/stack/stack";
import {
  TimeRangePicker,
  TimeRange,
} from "@/core/components/ui/time-range-picker";
import { toast } from "@/core/components/ui/toast";
import {
  HeaderStack,
  MainCard,
  MainStack,
} from "@/business-settings/common/business-settings-page";
import { PageHeader } from "@/business-settings/common/page-header";
import { SIDEBAR_MODES, SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { dayjs } from "@/core/utils/date";
import { multiFieldSearch } from "@/core/utils/multi-field-search";
import AddReferralModal from "@/referrals/common/add-referral-modal";
import { PayoutModal } from "@/referrals/common/referral-payout-modal";
import {
  ACTIVE_REFERRAL_STATUSES,
  REFERRAL_FIELD,
} from "@/referrals/constants";
import { REFERRAL_FIELD_TO_SORTER } from "@/referrals/constants";
import { trackReferralUpdate } from "@/referrals/events";
import {
  useGetReferrals,
  useUpdateReferral,
  useDeleteReferral,
} from "@/referrals/query";
import { IReferral, ReferralUpdateParams } from "@/referrals/referral";
import { REFERRAL_SEARCH_FIELDS } from "@/referrals/search-constants";

const ReferralHistoryContent = () => {
  const { businessId } = useParams();

  const { data: referrals = [], isLoading: isLoadingReferrals } =
    useGetReferrals(businessId);

  const posthog = usePostHog();
  const {
    state: { selectedItemId, payoutReferral },
    updateSidebar,
  } = useBusinessSettingsSidebar();

  const selectedReferral = useMemo(
    () => referrals.find((r) => r.id === selectedItemId) || null,
    [referrals, selectedItemId],
  );

  const [searchValue, setSearchValue] = useState("");
  const [onlyActiveReferrals, setOnlyActiveReferrals] = useState(false);
  const [addReferralModalOpen, setAddReferralModalOpen] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange | null>(null);

  const { mutate: updateReferral } = useUpdateReferral(businessId);
  const { mutate: deleteReferral } = useDeleteReferral(businessId);

  const handleUpdateReferral = useCallback(
    (referralId: string, values: Partial<ReferralUpdateParams>) => {
      updateReferral(
        {
          id: referralId,
          updates: values,
        },
        {
          onSuccess: () => {
            if (values.status === "LOST") {
              toast.success("Referral marked as lost");
            } else {
              toast.success("Referral updated successfully");
            }
            const updatedReferral = referrals.find((r) => r.id === referralId);
            if (updatedReferral) {
              trackReferralUpdate(posthog, {
                referral: updatedReferral,
                updates: values,
                method: "sidebar",
                success: true,
              });
            }
          },
          onError: (error) => {
            const updatedReferral = referrals.find((r) => r.id === referralId);
            if (updatedReferral) {
              trackReferralUpdate(posthog, {
                referral: updatedReferral,
                updates: values,
                method: "sidebar",
                success: false,
                error,
              });
            }
            toast.error("Failed to update referral");
          },
        },
      );
    },
    [updateReferral, toast, posthog, referrals],
  );

  const handleDeleteReferral = useCallback(
    (referralId: string) => {
      deleteReferral(
        {
          id: referralId,
        },
        {
          onSuccess: () => {
            toast.success("Referral deleted successfully");
            updateSidebar({
              isOpen: false,
              section: SIDEBAR_SECTIONS.referralHistory,
              mode: SIDEBAR_MODES.view,
              selectedItemId: undefined,
            });
          },
          onError: () => {
            toast.error("Failed to delete referral");
          },
        },
      );
    },
    [deleteReferral, toast, updateSidebar],
  );

  const handleRowClick = useCallback(
    (referral: IReferral) => {
      updateSidebar({
        section: SIDEBAR_SECTIONS.referralHistory,
        mode: SIDEBAR_MODES.view,
        selectedItemId: referral.id,
        isOpen: true,
      });
    },
    [updateSidebar],
  );

  const sortedFilteredReferrals = useMemo(() => {
    let filtered = onlyActiveReferrals
      ? referrals.filter((referral) =>
          ACTIVE_REFERRAL_STATUSES.includes(referral.status),
        )
      : [...referrals];

    // Apply search filter using multi-field search
    if (searchValue) {
      filtered = multiFieldSearch(
        filtered,
        searchValue,
        REFERRAL_SEARCH_FIELDS,
      );
    }

    if (timeRange) {
      const from = dayjs(timeRange.from).startOf("day");
      const to = dayjs(timeRange.to).endOf("day");
      filtered = filtered.filter((referral) => {
        const referralDate = dayjs(referral.created_at);
        return (
          referralDate.isSameOrAfter(from) && referralDate.isSameOrBefore(to)
        );
      });
    }

    return filtered.sort(REFERRAL_FIELD_TO_SORTER[REFERRAL_FIELD.status]);
  }, [timeRange, onlyActiveReferrals, referrals, searchValue]);

  const isReferralsEmpty = referrals.length === 0 && !isLoadingReferrals;

  return (
    <>
      <Stack className="mr-5" gap="sm" fullWidth>
        <HeaderStack>
          <Stack direction="row" align="center">
            <PageHeader className="inline-flex items-center gap-2">
              Referrals{" "}
              {!isReferralsEmpty && (
                <LoadingCount
                  count={sortedFilteredReferrals.length}
                  isCountLoading={isLoadingReferrals}
                  variant="subtle"
                />
              )}
            </PageHeader>
            {!isReferralsEmpty && (
              <SearchInput
                placeholder="Search Referrals"
                value={searchValue}
                onChange={setSearchValue}
              />
            )}
          </Stack>
          <Stack direction="row" gap="lg">
            {!isReferralsEmpty && (
              <>
                <SwitchWithLabel
                  label="Active referrals only"
                  checked={onlyActiveReferrals}
                  onChange={setOnlyActiveReferrals}
                />
                <TimeRangePicker
                  value={timeRange}
                  onChange={setTimeRange}
                  placeholder="Select date range"
                />
              </>
            )}

            <Button
              size="sm"
              variant="secondary"
              leftIcon={<PlusIcon className="h-4 w-4 min-w-4" />}
              onClick={() => setAddReferralModalOpen(true)}
            >
              Add Referral
            </Button>
          </Stack>
        </HeaderStack>
        <MainCard>
          <MainStack>
            {isReferralsEmpty ? (
              <EmptyTableMessage />
            ) : (
              <ReferralTable
                referrals={sortedFilteredReferrals}
                isLoading={isLoadingReferrals}
                searchValue={searchValue}
                selectedReferral={selectedReferral}
                setSearchValue={setSearchValue}
                onRowClick={handleRowClick}
                setPayoutReferral={(referral) =>
                  updateSidebar({ payoutReferral: referral })
                }
                className="h-full"
                onLost={(referralId) =>
                  handleUpdateReferral(referralId, { status: "LOST" })
                }
                onMoveToPaid={(referralId) =>
                  handleUpdateReferral(referralId, { status: "PAID" })
                }
                onDelete={handleDeleteReferral}
              />
            )}
          </MainStack>
        </MainCard>
      </Stack>
      <PayoutModal
        referral={payoutReferral}
        onOpenChange={(referral) => updateSidebar({ payoutReferral: referral })}
      />
      <AddReferralModal
        businessId={businessId}
        open={addReferralModalOpen}
        onOpenChange={setAddReferralModalOpen}
        onSuccess={(data) => {
          updateSidebar({
            mode: SIDEBAR_MODES.view,
            section: SIDEBAR_SECTIONS.referralHistory,
            selectedItemId: data.id,
            isOpen: true,
          });
        }}
      />
    </>
  );
};

const ReferralHistory = () => (
  <HiddenWhenSpiffOnly fallbackPath="../">
    <ReferralHistoryContent />
  </HiddenWhenSpiffOnly>
);

export default ReferralHistory;
